import React, { useState, useEffect } from 'react';
import Icon from 'components/_UI/Icon';
import colors from 'theme/colors';
import { ui } from 'theme/discover';
import { useEstablishmentFilter } from 'context/establishments/EstablishmentFilter';
import { useUserLocation } from 'context/user/UserLocation';
import establishmentsApi from 'api/requests/establishments';
import { getCategoryIcon } from 'modules/events/categories/categoryIconMap';
import { transformedDateFilters } from 'theme/filters';
import FilterSheet, {
    ChipsWrap,
    FilterChip,
    FilterLoading,
    FilterRange,
    FilterSection,
} from 'modules/_common/filters/FilterSheet';

const MIN_RANGE = 1;
const MAX_RANGE = 50;
const DEFAULT_RANGE = 25;

function selectedHint(count) {
    return count ? `${count} selecionada${count > 1 ? 's' : ''}` : undefined;
}

export default function EstablishmentFiltersModal({ isOpen, onClose }) {
    const { establishmentFilter, setEstablishmentFilter } = useEstablishmentFilter();
    const { userLocation } = useUserLocation();

    const [distance, setDistance] = useState(establishmentFilter?.distance_range || DEFAULT_RANGE);
    const [date, setDate] = useState(establishmentFilter?.dates || null);
    const [selectedTags, setSelectedTags] = useState(establishmentFilter?.tags || []);
    const [selectedFeatures, setSelectedFeatures] = useState(establishmentFilter?.features || []);
    const [tags, setTags] = useState([]);
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchOptions();
            setDistance(establishmentFilter?.distance_range || DEFAULT_RANGE);
            setDate(establishmentFilter?.dates || null);
            setSelectedTags(establishmentFilter?.tags || []);
            setSelectedFeatures(establishmentFilter?.features || []);
        }
    }, [isOpen]);

    async function fetchOptions() {
        setLoading(true);
        try {
            const { data } = await establishmentsApi.getOptions();
            setTags(data?.tags || []);
            setFeatures(data?.features || []);
        } catch (error) {
            console.error('Error fetching establishment options:', error);
        } finally {
            setLoading(false);
        }
    }

    function toggleTag(id) {
        setSelectedTags(prev =>
            prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
        );
    }

    function toggleFeature(id) {
        setSelectedFeatures(prev =>
            prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
        );
    }

    // Clicar de novo no período ativo desmarca: é o único jeito de voltar a "qualquer data".
    function toggleDate(value) {
        setDate(prev => prev === value ? null : value);
    }

    function handleClear() {
        setDistance(DEFAULT_RANGE);
        setDate(null);
        setSelectedTags([]);
        setSelectedFeatures([]);
        setEstablishmentFilter(null);
        onClose();
    }

    const hasLocation = !!(userLocation?.lat && userLocation?.long);

    const hasLocalFilters = !!date || selectedTags.length > 0 || selectedFeatures.length > 0 ||
        (userLocation?.lat && distance !== DEFAULT_RANGE);

    function handleApply() {
        setEstablishmentFilter(hasLocalFilters ? {
            distance_range: userLocation?.lat ? distance : null,
            lat: userLocation?.lat || null,
            long: userLocation?.long || null,
            dates: date,
            tags: selectedTags,
            features: selectedFeatures,
        } : null);
        onClose();
    }

    return (
        <FilterSheet
            isOpen={isOpen}
            onClose={onClose}
            canClear={!!hasLocalFilters || !!establishmentFilter}
            onClear={handleClear}
            onApply={handleApply}
        >
            {hasLocation && (
                <FilterSection title='Distância' hint={`${distance} km`}>
                    <FilterRange
                        value={distance}
                        min={MIN_RANGE}
                        max={MAX_RANGE}
                        onChange={setDistance}
                    />
                </FilterSection>
            )}

            <FilterSection title='Quando'>
                <ChipsWrap>
                    {transformedDateFilters.map(opt => (
                        <FilterChip
                            key={opt.value}
                            label={opt.label}
                            active={date === opt.value}
                            onClick={() => toggleDate(opt.value)}
                        />
                    ))}
                </ChipsWrap>
            </FilterSection>

            {loading ? (
                <FilterSection title='Características do local'>
                    <FilterLoading>Carregando...</FilterLoading>
                </FilterSection>
            ) : (
                <>
                    {features.length > 0 && (
                        <FilterSection
                            title='Características do local'
                            hint={selectedHint(selectedFeatures.length)}
                        >
                            <ChipsWrap>
                                {features.map(feature => {
                                    const isSelected = selectedFeatures.includes(feature.id);
                                    return (
                                        <FilterChip
                                            key={feature.id}
                                            label={feature.name}
                                            icon={(
                                                <Icon
                                                    name={feature.icon}
                                                    color={isSelected ? colors.neutralLight : ui.link}
                                                />
                                            )}
                                            active={isSelected}
                                            onClick={() => toggleFeature(feature.id)}
                                        />
                                    );
                                })}
                            </ChipsWrap>
                        </FilterSection>
                    )}

                    {tags.length > 0 && (
                        <FilterSection
                            title='Categorias'
                            hint={selectedHint(selectedTags.length)}
                        >
                            <ChipsWrap>
                                {tags.map(tag => {
                                    const TagIcon = getCategoryIcon(tag.icon);
                                    return (
                                        <FilterChip
                                            key={tag.id}
                                            label={tag.name}
                                            icon={<TagIcon />}
                                            active={selectedTags.includes(tag.id)}
                                            onClick={() => toggleTag(tag.id)}
                                        />
                                    );
                                })}
                            </ChipsWrap>
                        </FilterSection>
                    )}
                </>
            )}
        </FilterSheet>
    );
}
